/* eslint-disable no-restricted-syntax */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, InputGroup, Form } from "react-bootstrap";
import "./css/FamilyTreeTest.css";
import { parseGedFile, buildFamilyTree, getMostRelevantRoot } from "../utils/gedParser";
import { runAllTests } from "../utils/gedParser.test";

const FamilyTreeTest = () => {
    const [treeData, setTreeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [searchTerm, setSearchTerm] = useState("");
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const nodePositionsRef = useRef(new Map());
    const generationsRef = useRef(new Map());
    const photoCacheRef = useRef({});

    /**
     * Helper function to draw rounded rectangle
     */
    const drawRoundRect = (ctx, x, y, width, height, radius) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    };

    /**
     * Format date string to "DD MONTH YEAR" format
     */
    const formatDate = (dateStr) => {
        if (!dateStr) return "Unknown";
        const parts = dateStr.trim().split(/\s+/);
        if (parts.length === 3) {
            const [day, month, year] = parts;
            const monthNames = {
                JAN: "January",
                FEB: "February",
                MAR: "March",
                APR: "April",
                MAY: "May",
                JUN: "June",
                JUL: "July",
                AUG: "August",
                SEP: "September",
                OCT: "October",
                NOV: "November",
                DEC: "December",
            };
            return `${day} ${monthNames[month] || month} ${year}`;
        }
        return dateStr;
    };

    /**
     * Calculate generation depth for each person
     */
    const calculateGenerations = (people, rootPerson) => {
        const generations = new Map();

        const calculateDepth = (person, depth = 0) => {
            if (generations.has(person.id)) return;
            generations.set(person.id, depth);

            if (person.children && person.children.length > 0) {
                person.children.forEach((child) => {
                    calculateDepth(child, depth + 1);
                });
            }
        };

        if (rootPerson) {
            calculateDepth(rootPerson);
        }

        // Calculate parents/ancestors
        people.forEach((person) => {
            if (!generations.has(person.id) && person.parents && person.parents.length > 0) {
                const parentDepth = Math.min(...person.parents.map((p) => generations.get(p.id) ?? 0));
                calculateDepth(person, Math.max(parentDepth - 1, -people.length));
            }
        });

        return generations;
    };

    /**
     * Get color based on generation (lighter = younger, darker = older)
     */
    const getGenerationColor = (generation, maxGen, minGen) => {
        // Normalize generation to 0-1 range
        const normalized = (generation - minGen) / (maxGen - minGen || 1);
        // Create gradient from light blue (young) to dark blue (old)
        const lightR = 230;
        const lightG = 245;
        const lightB = 255;
        const darkR = 30;
        const darkG = 70;
        const darkB = 150;

        const r = Math.round(lightR + (darkR - lightR) * normalized);
        const g = Math.round(lightG + (darkG - lightG) * normalized);
        const b = Math.round(lightB + (darkB - lightB) * normalized);

        return `rgb(${r}, ${g}, ${b})`;
    };

    /**
     * Deduplicate people and build tree structure
     */
    const deduplicateAndBuildTree = (people) => {
        const seenIds = new Set();
        const uniquePeople = [];

        const roots = people.filter((p) => !p.parents || p.parents.length === 0);

        const processedIds = new Set();

        const processPerson = (person) => {
            if (processedIds.has(person.id)) return;
            processedIds.add(person.id);

            if (!seenIds.has(person.id)) {
                seenIds.add(person.id);
                uniquePeople.push(person);
            }

            if (person.children) {
                person.children.forEach((child) => {
                    processPerson(child);
                });
            }
        };

        roots.forEach((root) => {
            processPerson(root);
        });

        // Add any remaining people not yet included (disconnected or orphaned)
        people.forEach((person) => {
            if (!seenIds.has(person.id)) {
                seenIds.add(person.id);
                uniquePeople.push(person);
                // Also process their descendants
                processPerson(person);
            }
        });

        const rootPerson = getMostRelevantRoot(uniquePeople);

        return {
            people: uniquePeople,
            rootPerson,
        };
    };

    /**
     * Calculate hierarchical layout positions with better spacing
     */
    const calculateTreeLayout = (rootPerson, allPeople) => {
        const positions = new Map();
        const nodeWidth = 140;
        const nodeHeight = 200;
        const verticalGap = 300;
        const horizontalGap = 180;

        const positioned = new Set();

        const positionPerson = (person, x, y) => {
            if (positioned.has(person.id)) return;
            positioned.add(person.id);
            positions.set(person.id, { x, y });

            // Position children with much better spacing
            if (person.children && person.children.length > 0) {
                const childrenCount = person.children.length;
                // Increased spacing multiplier to prevent overlaps
                let currentX = x - (childrenCount * (nodeWidth + horizontalGap)) / 2 + nodeWidth / 2;

                person.children.forEach((child) => {
                    const childX = currentX;
                    const childY = y + verticalGap;
                    positionPerson(child, childX, childY);
                    currentX += nodeWidth + horizontalGap;
                });
            }
        };

        if (rootPerson) {
            positionPerson(rootPerson, 0, 0);
        }

        // Position any remaining people not reachable from root (disconnected groups)
        let groupIndex = 0;
        allPeople.forEach((person) => {
            if (!positioned.has(person.id)) {
                // Start new tree groups at different positions
                const xOffset = groupIndex * 500;
                positionPerson(person, xOffset, 0);
                groupIndex += 1;
            }
        });

        return { positions, nodeWidth, nodeHeight };
    };

    /**
     * Load and extract photo path from GEDCOM OBJE
     */
    const loadPhotoForPerson = async (photoId) => {
        if (!photoId) return null;
        if (photoCacheRef.current[photoId]) return photoCacheRef.current[photoId];

        try {
            const response = await fetch("/familytreedata/Fedorovsky_2025-03-23.ged");
            const gedContent = await response.text();

            const lines = gedContent.split("\n");
            let inObject = false;
            let photoPath = null;

            for (let i = 0; i < lines.length; i += 1) {
                const line = lines[i];
                if (line.includes(`@${photoId}@ OBJE`)) {
                    inObject = true;
                } else if (inObject && line.includes("1 FILE")) {
                    const pathMatch = line.match(/FILE\s+(.+)/);
                    if (pathMatch) {
                        let filePath = pathMatch[1].trim();
                        // Extract just the filename
                        filePath = filePath.split("\\").pop().split("/").pop();
                        photoPath = `/familytreedata/photos/${filePath}`;
                        break;
                    }
                } else if (inObject && line.startsWith("0")) {
                    break;
                }
            }

            photoCacheRef.current[photoId] = photoPath;
            return photoPath;
        } catch (error) {
            console.error("Error loading photo:", error);
            return null;
        }
    };

    /**
     * Draw the family tree with modern sleek tiles and marriage relationships
     */
    const drawTree = (ctx, canvas) => {
        if (!treeData || !treeData.rootPerson) {
            console.log("drawTree: No tree data available");
            return;
        }

        const { positions, nodeWidth, nodeHeight } = calculateTreeLayout(treeData.rootPerson, treeData.people);
        nodePositionsRef.current = new Map(positions);

        const generations = calculateGenerations(treeData.people, treeData.rootPerson);
        generationsRef.current = generations;

        const allGenerations = Array.from(generations.values());
        const maxGen = Math.max(...allGenerations, 0);
        const minGen = Math.min(...allGenerations, 0);

        // Clear canvas
        ctx.fillStyle = "#f8f9fa";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Find bounds
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        positions.forEach((pos) => {
            minX = Math.min(minX, pos.x);
            maxX = Math.max(maxX, pos.x);
            minY = Math.min(minY, pos.y);
            maxY = Math.max(maxY, pos.y);
        });

        // Handle empty positions
        if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
            minX = -300;
            maxX = 300;
            minY = -300;
            maxY = 300;
        }

        // Center the tree
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        // Better centering logic
        const offsetX = centerX - minX * zoom + pan.x;
        const offsetY = centerY - minY * zoom + pan.y;

        // Draw parent-child connection lines
        ctx.strokeStyle = "#b0b8c1";
        ctx.lineWidth = 1.5;

        positions.forEach((parentPos, personId) => {
            const person = treeData.people.find((p) => p.id === personId);
            if (person && person.children) {
                person.children.forEach((child) => {
                    const childPos = positions.get(child.id);
                    if (childPos) {
                        const x1 = parentPos.x * zoom + offsetX;
                        const y1 = parentPos.y * zoom + offsetY;
                        const x2 = childPos.x * zoom + offsetX;
                        const y2 = childPos.y * zoom + offsetY;

                        ctx.beginPath();
                        ctx.moveTo(x1, y1 + nodeHeight / 2);
                        ctx.lineTo(x1, (y1 + y2) / 2);
                        ctx.lineTo(x2, (y1 + y2) / 2);
                        ctx.lineTo(x2, y2);
                        ctx.stroke();
                    }
                });
            }
        });

        // Draw marriage/spouse relationships (horizontal lines)
        ctx.strokeStyle = "#e74c3c";
        ctx.lineWidth = 2;
        const drawnMarriages = new Set();

        positions.forEach((pos, personId) => {
            const person = treeData.people.find((p) => p.id === personId);
            if (person && person.spouse) {
                const marriageKey = [personId, person.spouse.id].sort().join("-");
                if (!drawnMarriages.has(marriageKey)) {
                    drawnMarriages.add(marriageKey);
                    const spousePos = positions.get(person.spouse.id);
                    if (spousePos) {
                        const x1 = pos.x * zoom + offsetX;
                        const y1 = pos.y * zoom + offsetY;
                        const x2 = spousePos.x * zoom + offsetX;
                        const y2 = spousePos.y * zoom + offsetY;

                        ctx.beginPath();
                        ctx.moveTo(x1 + nodeWidth / 2, y1);
                        ctx.lineTo(x2 - nodeWidth / 2, y2);
                        ctx.stroke();
                    }
                }
            }
        });

        // Draw person nodes with modern sleek tiles
        positions.forEach((pos, personId) => {
            const person = treeData.people.find((p) => p.id === personId);
            if (!person) return;

            const x = pos.x * zoom + offsetX;
            const y = pos.y * zoom + offsetY;
            const isSelected = selectedPerson && selectedPerson.id === personId;

            // Modern card design with shadow
            const cardLeft = x - nodeWidth / 2;
            const cardTop = y - nodeHeight / 2;

            // Drop shadow
            ctx.fillStyle = "rgba(0, 0, 0, 0.08)";
            ctx.fillRect(cardLeft + 2, cardTop + 2, nodeWidth, nodeHeight);

            // Card background
            ctx.fillStyle = isSelected ? "#e3f2fd" : "#ffffff";
            ctx.fillRect(cardLeft, cardTop, nodeWidth, nodeHeight);

            // Selection border
            if (isSelected) {
                ctx.strokeStyle = "#1976d2";
                ctx.lineWidth = 2;
                ctx.strokeRect(cardLeft, cardTop, nodeWidth, nodeHeight);
            } else {
                ctx.strokeStyle = "#e0e0e0";
                ctx.lineWidth = 1;
                ctx.strokeRect(cardLeft, cardTop, nodeWidth, nodeHeight);
            }

            // Photo area with rounded corners
            const photoY = cardTop + 6;
            const photoX = cardLeft + 6;
            const photoWidth = nodeWidth - 12;
            const photoHeight = 70;

            // Rounded rectangle background
            ctx.fillStyle = "#f5f5f5";
            drawRoundRect(ctx, photoX, photoY, photoWidth, photoHeight, 4);
            ctx.fill();

            // Try to draw photo if available
            if (person.photoId && photoCacheRef.current[person.photoId] !== undefined) {
                const photoPath = photoCacheRef.current[person.photoId];
                if (photoPath && photoCacheRef.current[`img_${person.photoId}`]) {
                    const img = photoCacheRef.current[`img_${person.photoId}`];
                    if (img && img.complete) {
                        try {
                            // Draw image with rounded corners
                            ctx.save();
                            drawRoundRect(ctx, photoX, photoY, photoWidth, photoHeight, 4);
                            ctx.clip();
                            ctx.drawImage(img, photoX, photoY, photoWidth, photoHeight);
                            ctx.restore();
                        } catch (e) {
                            // Image failed to load
                        }
                    }
                }
            }

            // Draw name with improved typography
            ctx.fillStyle = "#1a1a1a";
            ctx.font = "bold 10px 'Segoe UI', Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "top";

            const name = person.fullName || `${person.givenName || ""} ${person.surname || ""}`;
            const displayName = name.length > 15 ? `${name.substring(0, 12)}...` : name;
            ctx.fillText(displayName, x, photoY + photoHeight + 8, nodeWidth - 12);

            // Draw birth and death info with better formatting
            ctx.font = "7px 'Segoe UI', Arial";
            ctx.fillStyle = "#666666";
            let infoY = photoY + photoHeight + 24;

            if (person.birthData && person.birthData.date) {
                const birthDate = person.birthData.date.trim();
                const yearMatch = birthDate.match(/(\d{4})/);
                if (yearMatch) {
                    ctx.fillText(`b. ${yearMatch[1]}`, x, infoY, nodeWidth - 14);
                    infoY += 7;
                }
            }

            if (person.birthData && person.birthData.place) {
                const place = person.birthData.place.trim();
                const placeParts = place.split(",").map((p) => p.trim());
                const shortPlace = placeParts.slice(-1)[0];
                ctx.fillText(shortPlace.substring(0, 13), x, infoY, nodeWidth - 14);
                infoY += 7;
            }

            if (person.deathData && person.deathData.date) {
                const deathDate = person.deathData.date.trim();
                const yearMatch = deathDate.match(/(\d{4})/);
                if (yearMatch) {
                    ctx.fillText(`d. ${yearMatch[1]}`, x, infoY, nodeWidth - 14);
                    infoY += 7;
                }
            }

            if (person.deathData && person.deathData.place) {
                const place = person.deathData.place.trim();
                const placeParts = place.split(",").map((p) => p.trim());
                const shortPlace = placeParts.slice(-1)[0];
                ctx.fillText(shortPlace.substring(0, 13), x, infoY, nodeWidth - 14);
            }
        });
    };

    // Load and parse GED file
    useEffect(() => {
        const fetchGedFile = async () => {
            try {
                console.log("Running GED Parser unit tests...");
                runAllTests();

                const response = await fetch("/familytreedata/Fedorovsky_2025-03-23.ged");
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const gedContent = await response.text();
                const { individuals, families } = parseGedFile(gedContent);
                const people = buildFamilyTree(individuals, families);

                // Deduplicate and build tree
                const deduplicatedTree = deduplicateAndBuildTree(people);
                setTreeData(deduplicatedTree);
            } catch (error) {
                console.error("FamilyTreeTest: Error loading GED file:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchGedFile();
    }, []);

    // Canvas drawing logic
    useEffect(() => {
        if (!canvasRef.current || !treeData) {
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        // Set canvas size to match container
        const container = containerRef.current;
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;

        // Draw tree
        drawTree(ctx, canvas);
    }, [treeData, zoom, pan, selectedPerson]);

    // Load photos for people
    useEffect(() => {
        if (!treeData) return;

        const loadPhotos = async () => {
            const response = await fetch("/familytreedata/Fedorovsky_2025-03-23.ged");
            const gedContent = await response.text();

            for (const person of treeData.people) {
                if (person.photoId) {
                    // Extract photo path
                    if (!photoCacheRef.current[person.photoId]) {
                        const lines = gedContent.split("\n");
                        let inObject = false;
                        let photoPath = null;

                        for (let i = 0; i < lines.length; i += 1) {
                            const line = lines[i];
                            if (line.includes(`@${person.photoId}@ OBJE`)) {
                                inObject = true;
                            } else if (inObject && line.includes("1 FILE")) {
                                const pathMatch = line.match(/FILE\s+(.+)/);
                                if (pathMatch) {
                                    let filePath = pathMatch[1].trim();
                                    filePath = filePath.split("\\").pop().split("/").pop();
                                    photoPath = `/familytreedata/photos/${filePath}`;
                                    break;
                                }
                            } else if (inObject && line.startsWith("0")) {
                                break;
                            }
                        }

                        photoCacheRef.current[person.photoId] = photoPath;

                        // Load the actual image
                        if (photoPath) {
                            const img = new Image();
                            img.crossOrigin = "anonymous";
                            img.onload = () => {
                                photoCacheRef.current[`img_${person.photoId}`] = img;
                                // Trigger re-render
                                if (canvasRef.current) {
                                    const ctx = canvasRef.current.getContext("2d");
                                    drawTree(ctx, canvasRef.current);
                                }
                            };
                            img.onerror = () => {
                                console.warn(`Failed to load photo: ${photoPath}`);
                            };
                            img.src = photoPath;
                        }
                    }
                }
            }
        };

        loadPhotos().catch((error) => {
            console.error("Error loading photos:", error);
        });
    }, [treeData]);

    // Handle canvas click
    const handleCanvasClick = (e) => {
        if (!canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        // Check which person was clicked
        nodePositionsRef.current.forEach((pos, personId) => {
            const person = treeData.people.find((p) => p.id === personId);
            if (!person) return;

            const nodeWidth = 140;
            const nodeHeight = 200;

            // Find bounds for centering
            let minX = Infinity;
            let maxX = -Infinity;
            let minY = Infinity;
            let maxY = -Infinity;
            nodePositionsRef.current.forEach((p) => {
                minX = Math.min(minX, p.x);
                maxX = Math.max(maxX, p.x);
                minY = Math.min(minY, p.y);
                maxY = Math.max(maxY, p.y);
            });

            const canvas = canvasRef.current;
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const offsetX = centerX - ((minX + maxX) / 2) * zoom + pan.x;
            const offsetY = centerY - ((minY + maxY) / 2) * zoom + pan.y;

            const x = pos.x * zoom + offsetX;
            const y = pos.y * zoom + offsetY;

            if (clickX >= x - nodeWidth / 2 && clickX <= x + nodeWidth / 2 && clickY >= y - nodeHeight / 2 && clickY <= y + nodeHeight / 2) {
                setSelectedPerson(person);
                setShowSearchDropdown(false);
            }
        });
    };

    // Handle mouse wheel zoom with constraints to prevent blank space
    const handleWheel = (e) => {
        e.preventDefault();
        const zoomSpeed = 0.1;
        // Min zoom ensures at least one person is visible, max zoom is 3x
        const minZoom = 0.3;
        const maxZoom = 3;
        const newZoom = e.deltaY < 0 ? Math.min(maxZoom, zoom + zoomSpeed) : Math.max(minZoom, zoom - zoomSpeed);
        setZoom(newZoom);
    };

    // Handle pan (drag)
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        setPan({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Reset view
    const handleResetView = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setSelectedPerson(null);
    };

    // Center on selected person
    const handleCenterOnPerson = () => {
        if (!selectedPerson || !canvasRef.current) return;

        const pos = nodePositionsRef.current.get(selectedPerson.id);
        if (!pos) return;

        const canvas = canvasRef.current;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        // Proper centering: pan should move the position to center
        setPan({
            x: centerX - pos.x * zoom,
            y: centerY - pos.y * zoom,
        });
    };

    // Filter people based on search
    const filteredPeople = useMemo(() => {
        if (!treeData || !searchTerm.trim()) {
            return [];
        }

        const lowerSearch = searchTerm.toLowerCase();
        return treeData.people.filter((p) => (p.fullName && p.fullName.toLowerCase().includes(lowerSearch)) || (p.givenName && p.givenName.toLowerCase().includes(lowerSearch)) || (p.surname && p.surname.toLowerCase().includes(lowerSearch))).slice(0, 10);
    }, [treeData, searchTerm]);

    const handleSelectFromSearch = (person) => {
        setSelectedPerson(person);
        setSearchTerm("");
        setShowSearchDropdown(false);
        // Center on the selected person
        setTimeout(() => {
            const pos = nodePositionsRef.current.get(person.id);
            if (pos && canvasRef.current) {
                const canvas = canvasRef.current;
                const centerX = canvas.width / 2;
                const centerY = canvas.height / 2;
                setPan({
                    x: centerX - pos.x * zoom,
                    y: centerY - pos.y * zoom,
                });
            }
        }, 0);
    };

    if (loading) {
        return (
            <div className="family-tree-container" style={{ textAlign: "center", paddingTop: "50px" }}>
                <p>Loading family tree data...</p>
            </div>
        );
    }

    if (!treeData) {
        return (
            <div className="family-tree-container" style={{ textAlign: "center", paddingTop: "50px" }}>
                <p>Error loading family tree data</p>
            </div>
        );
    }

    return (
        <div className="family-tree-wrapper">
            <div className="family-tree-main" ref={containerRef}>
                <div className="tree-controls">
                    <Button variant="secondary" size="sm" onClick={handleResetView}>
                        Reset View
                    </Button>
                    {selectedPerson && (
                        <Button variant="info" size="sm" onClick={handleCenterOnPerson}>
                            Center on Person
                        </Button>
                    )}
                    <span className="zoom-indicator">{`Zoom: ${zoom.toFixed(1)}x`}</span>
                </div>

                <div className="search-box">
                    <InputGroup size="sm">
                        <InputGroup.Text>🔍</InputGroup.Text>
                        <Form.Control
                            placeholder="Search family members..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setShowSearchDropdown(e.target.value.length > 0);
                            }}
                            onFocus={() => searchTerm.length > 0 && setShowSearchDropdown(true)}
                        />
                    </InputGroup>
                    {showSearchDropdown && filteredPeople.length > 0 && (
                        <div className="search-dropdown">
                            {filteredPeople.map((person) => (
                                <div key={person.id} className="search-dropdown-item" onClick={() => handleSelectFromSearch(person)}>
                                    {person.fullName || `${person.givenName} ${person.surname}`}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                        cursor: isDragging ? "grabbing" : "grab",
                        display: "block",
                        width: "100%",
                        height: "calc(100vh - 180px)",
                    }}
                />
            </div>

            {/* Sidebar with person details */}
            {selectedPerson && (
                <div className="sidebar-details">
                    <h2>{selectedPerson.fullName || `${selectedPerson.givenName} ${selectedPerson.surname}`}</h2>

                    {selectedPerson.birthData && (
                        <div className="detail-section">
                            <h5>Birth</h5>
                            <p>
                                <strong>Date:</strong> {formatDate(selectedPerson.birthData.date) || "Unknown"}
                            </p>
                            <p>
                                <strong>Place:</strong> {selectedPerson.birthData.place || "Unknown"}
                            </p>
                        </div>
                    )}

                    {selectedPerson.deathData && (
                        <div className="detail-section">
                            <h5>Death</h5>
                            <p>
                                <strong>Date:</strong> {formatDate(selectedPerson.deathData.date) || "Unknown"}
                            </p>
                            <p>
                                <strong>Place:</strong> {selectedPerson.deathData.place || "Unknown"}
                            </p>
                        </div>
                    )}

                    {selectedPerson.parents && selectedPerson.parents.length > 0 && (
                        <div className="detail-section">
                            <h5>Parents</h5>
                            <ul>
                                {selectedPerson.parents.map((parent) => (
                                    <li key={parent.id} onClick={() => setSelectedPerson(parent)} className="clickable-link">
                                        {parent.fullName || `${parent.givenName} ${parent.surname}`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {selectedPerson.spouse && (
                        <div className="detail-section">
                            <h5>Spouse</h5>
                            <p onClick={() => setSelectedPerson(selectedPerson.spouse)} className="clickable-link">
                                {selectedPerson.spouse.fullName || `${selectedPerson.spouse.givenName} ${selectedPerson.spouse.surname}`}
                            </p>
                        </div>
                    )}

                    {selectedPerson.children && selectedPerson.children.length > 0 && (
                        <div className="detail-section">
                            <h5>
                                Children
                                {` (${selectedPerson.children.length})`}
                            </h5>
                            <ul>
                                {selectedPerson.children.map((child) => (
                                    <li key={child.id} onClick={() => setSelectedPerson(child)} className="clickable-link">
                                        {child.fullName || `${child.givenName} ${child.surname}`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FamilyTreeTest;
